import { useState } from 'preact/hooks';
import { useAppState, buildTFSAState, type LogEntry } from '../lib/state';
import { formatCAD, centsToDecimal } from '../lib/decimal';
import { localIsoNow } from '../lib/timezone';
import { LogEntryModal } from '../components/LogEntryModal';
import { Modal } from '../components/Modal';
import { ButtonPrimary } from '../components/ButtonPrimary';
import { ButtonSecondary } from '../components/ButtonSecondary';

export function History() {
  const { state, dispatch } = useAppState();
  const [filterYear, setFilterYear] = useState<string>('all');
  // v1.0.3 P1: per-row edit and delete. We track the target entry; the
  // modals are rendered conditionally based on which one is non-null.
  const [editingEntry, setEditingEntry] = useState<LogEntry | null>(null);
  const [deletingEntry, setDeletingEntry] = useState<LogEntry | null>(null);

  const allYears = [...new Set(state.logEntries.map((e) => e.date.slice(0, 4)))]
    .sort()
    .reverse();

  const filtered =
    filterYear === 'all'
      ? state.logEntries
      : state.logEntries.filter((e) => e.date.startsWith(filterYear));

  const sorted = [...filtered].sort((a, b) => b.date.localeCompare(a.date));

  // Build the TFSA state so the edit modal can render its over-contribution
  // logic against the user's real numbers. Edit mode suppresses the warning,
  // but the modal still needs a tfsa prop. Fall back to a minimal stub if
  // the wizard hasn't been completed (defensive; History is normally only
  // reachable post-wizard).
  const todayIso = localIsoNow();
  const tfsa = buildTFSAState(state, todayIso);

  return (
    <div class="max-w-md mx-auto px-4 py-6 space-y-4">
      <div class="flex items-center justify-between">
        <h1 class="text-xl font-bold text-hudson-navy">Activity history</h1>
        {allYears.length > 0 && (
          <label class="flex items-center gap-2 text-sm">
            <span class="sr-only">Filter by year</span>
            <select
              value={filterYear}
              aria-label="Filter by year"
              onChange={(e) => setFilterYear((e.target as HTMLSelectElement).value)}
              class="border border-card-border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-hudson-navy"
            >
              <option value="all">All years</option>
              {allYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {sorted.length === 0 ? (
        <div class="text-center py-12 border border-dashed border-card-border rounded-lg">
          <p class="text-sm text-body/70">
            No entries{filterYear !== 'all' ? ` in ${filterYear}` : ''}
          </p>
        </div>
      ) : (
        <ul class="divide-y divide-card-border border border-card-border rounded-lg overflow-hidden">
          {sorted.map((e) => (
            <li key={e.id} class="flex items-center justify-between px-4 py-3 bg-white gap-3">
              <div class="min-w-0">
                <span class="text-sm font-medium text-body">{e.date}</span>
                {e.institution && (
                  <span class="text-xs text-body/70 ml-2">{e.institution}</span>
                )}
                <span
                  class={`ml-2 text-xs font-medium px-1.5 py-0.5 rounded ${
                    e.type === 'contribution'
                      ? 'bg-white border border-success-green text-success-green'
                      : 'bg-white border border-alert-red text-alert-red'
                  }`}
                >
                  {e.type === 'contribution' ? 'Contribution' : 'Withdrawal'}
                </span>
              </div>
              <div class="flex items-center gap-2 shrink-0">
                {/* v1.0.4 P0 (defect B): always display the absolute magnitude
                    via formatCAD(abs(amount)) — never concatenate a hard-coded
                    "+" with the raw formatCAD result (which would emit
                    "+-$5,000" for a legacy negative contribution).
                    Sign: "+" only when this is a positive contribution
                    (an inflow). Withdrawals (always a debit) and any
                    non-positive contribution display "−". */}
                {(() => {
                  const cents = centsToDecimal(e.amount_cents);
                  const sign = e.type === 'contribution' && cents.greaterThan(0) ? '+' : '−';
                  const text = formatCAD(cents.abs().toFixed(0));
                  return (
                    <span
                      class={`font-bold ${
                        e.type === 'contribution' ? 'text-success-green' : 'text-alert-red'
                      }`}
                    >
                      <span aria-hidden="true">{sign}</span>
                      {text}
                    </span>
                  );
                })()}
                <button
                  type="button"
                  onClick={() => setEditingEntry(e)}
                  aria-label={`Edit ${e.type} from ${e.date}`}
                  class="text-body/70 hover:text-hudson-navy focus:outline-none focus:ring-2 focus:ring-hudson-navy rounded px-1.5 py-1"
                >
                  <span aria-hidden="true">✎</span>
                </button>
                <button
                  type="button"
                  onClick={() => setDeletingEntry(e)}
                  aria-label={`Delete ${e.type} from ${e.date}`}
                  class="text-body/70 hover:text-alert-red focus:outline-none focus:ring-2 focus:ring-alert-red rounded px-1.5 py-1"
                >
                  <span aria-hidden="true">🗑</span>
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editingEntry && tfsa && (
        <LogEntryModal
          mode={editingEntry.type}
          tfsa={tfsa}
          initialEntry={editingEntry}
          onClose={() => setEditingEntry(null)}
          onSubmit={(patched) => {
            dispatch({
              type: 'UPDATE_ENTRY',
              id: editingEntry.id,
              entry_type: editingEntry.type,
              patch: {
                amount_cents: patched.amount_cents,
                date: patched.date,
                institution: patched.institution,
              },
            });
            setEditingEntry(null);
          }}
        />
      )}

      {deletingEntry && (
        <Modal
          open
          title="Delete this entry?"
          onClose={() => setDeletingEntry(null)}
        >
          <div class="space-y-4">
            <p class="text-sm text-body">
              This permanently removes the {deletingEntry.type} of{' '}
              <span class="font-bold">{formatCAD(deletingEntry.amount_cents)}</span>{' '}
              on {deletingEntry.date}. The action can&apos;t be undone.
            </p>
            <div class="flex gap-3 pt-2">
              <ButtonSecondary onClick={() => setDeletingEntry(null)}>
                Cancel
              </ButtonSecondary>
              <ButtonPrimary
                onClick={() => {
                  dispatch({
                    type: 'DELETE_ENTRY',
                    id: deletingEntry.id,
                    entry_type: deletingEntry.type,
                  });
                  setDeletingEntry(null);
                }}
              >
                Delete
              </ButtonPrimary>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
